import moment from "moment";

export const QUARTERS = {
  1: [7, 8, 9], // Jul-Sep
  2: [10, 11, 12], // Oct-Dec
  3: [1, 2, 3], // Jan-Mar
  4: [4, 5, 6], // Apr-Jun
};

export function monthsOfQuarter(fyStartYear, q) {
  // Build the 3 month moments for the given FY and quarter
  const months = QUARTERS[q] || [];
  return months.map((m) => {
    // Month indexing in moment: 0..11, here m is 1..12 natural month
    // If m >= 7 => calendar year = fyStartYear, else fyStartYear+1
    const year = m >= 7 ? fyStartYear : fyStartYear + 1;
    const firstDay = moment({ year, month: m - 1, day: 1 });
    return {
      y: year,
      m,
      label: firstDay.format("MMM YYYY"),
      isoMonth: firstDay.format("YYYY-MM"),
      start: firstDay.startOf("month"),
      end: firstDay.endOf("month"),
    };
  });
}

/**
 * Derive per-month reporting statuses for "monthly" mode.
 * submissions: array with .createdOn (ISO) and optional .periodMonth ('YYYY-MM')
 */
export function computeMonthlyStatuses({
  months,
  submissions = [],
  now = moment(),
}) {
  const submittedSet = new Set(
    submissions.filter((s) => !!s.month).map((s) => s.month)
  );

  return months.map((m) => {
    const isSubmitted = submittedSet.has(m);
    const isPast = now.isAfter(m.end, "day");
    return {
      ...m,
      status: isSubmitted ? "reported" : isPast ? "missed" : "pending",
    };
  });
}

/**
 * RBM_CalculateScore Mirror.
 * qualityValue is supplied by supervisor (e.g. 95, 85, 75, 60, 40)
 */
export function rbmCalculateScore({
  measurementCode,
  target,
  achievedTarget,
  qualityValue = 95,
  isCompleted,
}) {
  let score;

  if (+target === 0) {
    if (measurementCode === "YN") {
      score = isCompleted ? 100 : achievedTarget || 0;
    } else {
      score = achievedTarget > 0 ? achievedTarget : 0;
    }
  } else {
    switch (measurementCode) {
      case "%UP":
      case "SUM":
      case "MS":
      case "POINTS":
        score = (achievedTarget / target) * 100;
        break;
      case "%DOWN":
        score = achievedTarget > 0 ? (target / achievedTarget) * 100 : 0;
        break;
      case "YN":
        score = isCompleted ? 100 : (achievedTarget / 100.0) * 100;
        break;
      case "REPEAT":
        score =
          achievedTarget >= target ? 100 : (achievedTarget / target) * 100;
        break;
      case "FIXED":
        score = achievedTarget === target ? 100 : 0;
        break;
      case "RANGE":
        score = achievedTarget === target ? 100 : 0;
        break;
      case "RANK":
        score = achievedTarget <= target ? 100 : 80;
        break;
      default:
        score = (achievedTarget / target) * 100;
    }
  }

  score = Math.max(0, Math.min(100, score));
  return Math.round(score * qualityValue) / 100;
}

/**
 * RBM_CalculateTimePenalty Mirror.
 * inputs:
 *  - itemTypeId (1,2,3)
 *  - deadline (ISO or moment)
 *  - submissions: [{ createdOn: ISO }]
 *  - rejections:  [{ createdOn: ISO }]
 * Notes:
 *  First late day = 5; subsequent late days = +1.
 *  After each rejection: anchor = rejection time + (1 day if in working hours [08:00-17:00], else +2 days)
 *  Then compare next submission time to the anchor for extra late days.
 */
export function rbmCalculateTimePenalty({
  deadline,
  submissions = [],
  rejections = [],
}) {
  if (!deadline || !submissions.length) return 0;

  const WorkStart = moment("08:00", "HH:mm");
  const WorkEnd = moment("17:00", "HH:mm");
  const dl = moment(deadline);

  const subs = submissions
    .map((s) => moment(s.createdOn))
    .filter((d) => d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  const rejs = rejections
    .map((r) => moment(r.createdOn))
    .filter((d) => d.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (!subs.length) return 0;

  let penalty = 0;
  let hasFirstPremium = false;

  // 1) initial lateness vs original deadline
  const firstSub = subs[0];
  const initialLateDays = Math.max(
    0,
    firstSub.startOf("day").diff(dl.startOf("day"), "days")
  );
  if (initialLateDays > 0) {
    penalty += 5 + (initialLateDays - 1);
    hasFirstPremium = true;
  }

  // 2) cycles after rejections
  let sumDays = 0;
  let firstLateDaysInCycles = null;

  rejs.forEach((rej) => {
    // anchor
    const t = moment(rej);
    const inWorkingHours = moment(t.format("HH:mm"), "HH:mm").isBetween(
      WorkStart,
      WorkEnd,
      null,
      "[]"
    );
    const anchor = moment(t)
      .add(inWorkingHours ? 1 : 2, "days")
      .startOf("day");

    // next submission after rej
    const nextSub = subs.find((s) => s.isAfter(rej));
    if (!nextSub) return;

    const daysLate = Math.max(0, nextSub.startOf("day").diff(anchor, "days"));
    if (daysLate > 0) {
      sumDays += daysLate;
      if (firstLateDaysInCycles == null) firstLateDaysInCycles = daysLate;
    }
  });

  if (sumDays > 0) {
    if (hasFirstPremium) {
      penalty += sumDays;
    } else {
      // first late day premium applies once
      penalty +=
        5 + (firstLateDaysInCycles - 1) + (sumDays - firstLateDaysInCycles);
    }
  }
  return penalty;
}

/**
 * Quality penalty: 5 marks per rejection (uncanceled).
 */
export function qualityPenaltyFromRejections({ rejectionsCount = 0 }) {
  return 5 * (rejectionsCount || 0);
}

/**
 * Expected Max Score for preview:
 *  - Computes core score via rbmCalculateScore
 *  - Multiplies by (activityWeight / totalWeight)
 *  - Applies penalties: time penalty + quality penalty
 *  - Clamps to [0,100]
 */
export function computeExpectedMaxScore({
  measurementCode,
  target,
  achievedTarget,
  qualityValue,
  isCompleted,
  activityWeight,
  totalWeight,
  timePenalty = 0,
  qualityPenalty = 0,
}) {
  const base = rbmCalculateScore({
    measurementCode,
    target: Number(target || 0),
    achievedTarget: Number(achievedTarget || 0),
    qualityValue: Number(qualityValue || 95) / 100,
    isCompleted: !!isCompleted,
  });

  const weighted =
    (Number(activityWeight || 0) / Number(totalWeight || 1)) * base;

  const penalized = Math.max(
    0,
    weighted - Number(timePenalty || 0) - Number(qualityPenalty || 0)
  );
  return Math.max(0, Math.min(100, Math.round(penalized * 100) / 100));
}

/**
 * Returns input constraints + UX hints for a measurement code.
 * forceEquals: when only an exact value is valid (e.g. FIXED).
 * isLowerBetter: use to flip warnings/hints (0 may be “best”).
 */
export const getMeasurementBounds = ({ code, target, extraParam }) => {
  const m = code;
  const t = Number(target ?? 0);

  switch (m) {
    // Higher is better, bounded by target
    case "SUM":
    case "MS":
    case "POINTS":
    case "REPEAT":
      return {
        min: 0,
        max: isFinite(t) && t > 0 ? t : 0,
        isLowerBetter: false,
        forceEquals: false,
        hint: `Allowed: 0 – ${t}. Higher is better.`,
      };

    // Percent improve: cap by target percent when provided, else 100
    case "%UP":
      return {
        min: 0,
        max: isFinite(t) && t > 0 ? t : 100,
        isLowerBetter: false,
        forceEquals: false,
        hint: `Allowed: 0 – ${
          isFinite(t) && t > 0 ? t : 100
        }%. Higher is better.`,
      };

    // Percent reduce: 0 is best; use target or 100 as upper bound for entry
    case "%DOWN":
      return {
        min: 0,
        max: isFinite(t) && t > 0 ? t : 100,
        isLowerBetter: true,
        forceEquals: false,
        hint: `Allowed: 0 – ${
          isFinite(t) && t > 0 ? t : 100
        }%. Lower is better (0 is best).`,
      };

    // Exact match to target only
    case "FIXED":
      return {
        min: 0,
        max: t,
        isLowerBetter: false,
        forceEquals: true,
        hint: `Must equal ${t}.`,
      };

    // Range target..extraParam (we'll hint if missing upper bound)
    case "RANGE":
      if (isFinite(extraParam)) {
        const lo = Math.min(t, extraParam);
        const hi = Math.max(t, extraParam);
        return {
          min: lo,
          max: hi,
          isLowerBetter: false,
          forceEquals: false,
          hint: `Allowed: ${lo} – ${hi}.`,
        };
      }
      return {
        min: 0,
        max: t,
        isLowerBetter: false,
        forceEquals: false,
        hint:
          extraParam == null
            ? `Expected between target bounds; upper bound missing.`
            : `Allowed up to ${t}.`,
      };

    // 1 = best rank (smaller is better). We accept integers >=1
    case "RANK":
      return {
        min: 1,
        max: isFinite(extraParam) && extraParam > 0 ? extraParam : undefined,
        isLowerBetter: true,
        forceEquals: false,
        hint:
          isFinite(extraParam) && extraParam > 0
            ? `Allowed: 1 – ${extraParam}. Lower rank is better.`
            : `Allowed: integers ≥ 1. Lower rank is better.`,
      };

    // Yes/No handled by your radio, but partial % (0–99) is allowed when “No”
    case "YN":
      return {
        min: 0,
        max: 100,
        isLowerBetter: false,
        forceEquals: false,
        hint: `If not completed, enter progress 0 – 99%.`,
      };

    default:
      // Safe fallback: non-negative, cap by target if present
      return {
        min: 0,
        max: isFinite(t) && t > 0 ? t : undefined,
        isLowerBetter: false,
        forceEquals: false,
        hint:
          isFinite(t) && t > 0
            ? `Allowed: 0 – ${t}.`
            : `Enter a non-negative value.`,
      };
  }
};

/**
 * Calculates the weight penalty for a performance metric.
 *
 * @param {number} target - The target value to achieve.
 * @param {number} achieved - The actual achieved value.
 * @param {number} weight - The weight assigned to this metric.
 * @param {string} measurementUnitCode - Unit code (e.g. "%DOWN", "%UP", etc.).
 * @param {number} [quality=1] - Optional quality multiplier.
 * @returns {{ ratio: number, weightPenalty: number, weightedScore: number, expected: number }}
 */
export const calculateWeightPenalty = ({
  target,
  achieved,
  weight,
  measurementUnitCode,
  quality = 1,
}) => {
  const targetVal = Number(target) || 0;
  const achievedVal = Number(achieved) || 0;
  const weightVal = Number(weight) || 1;
  const qualityVal = Number(quality) || 1;

  let ratio = 0;

  // Ratio calculation based on measurement type
  switch (measurementUnitCode) {
    case "%DOWN":
      if (targetVal <= 0) {
        ratio = 1;
      } else if (achievedVal <= targetVal) {
        ratio = 1;
      } else {
        ratio = Math.max(0, 1 - (achievedVal - targetVal) / 100);
      }
      break;

    default:
      ratio = targetVal > 0 ? achievedVal / targetVal : 1;
      break;
  }

  // Clamp ratio between 0 and 1
  ratio = Math.min(1, Math.max(0, ratio));

  // Calculate derived metrics
  const baseScore = ratio * 100;
  const weightPenalty = (1 - ratio) * weightVal;
  const weightedScore = Math.max(0, baseScore - weightPenalty);
  const qualityAdjusted = weightedScore * qualityVal;
  const expected = Math.max(0, Math.min(100, qualityAdjusted));

  return {
    ratio,
    baseScore,
    weightPenalty,
    weightedScore,
    expected,
    qualityAdjusted,
  };
};
